import React from 'react';
import { WixRicosViewer as Viewer } from '@wix/ricos';
import '@wix/ricos/css/ricos-viewer.global.css';
import classNames from 'classnames';
import { useApi } from '../../context/api-context/use-api';
import { getAppDefinitionId, getInstance, getIsMaInstalled, getIsMobile, isSeo as getIsSeo, } from '../../../common/store/base-params/base-params-selectors';
import { useConfiguration } from '../../context/configuration-context/use-configuration';
import { initViewerPlugins, getTransformedContent } from './ricos-utils';
import withError from '../../hoc/with-error';
import TextExtended from '../text-extended/text-extended';
import { useTranslate } from '../../context/api-context/translations/use-translate';
import { SHOW_MORE_BTN } from '../../../common/constants/data-hooks';
import { ArrowDownIcon, ArrowUpIcon } from '../icons/arrow-icon';
import MemberCardLazy from '../member-card/member-card-lazy';
import { getCustomStyles } from './ricos-styles';
import { isResizeObserverSupported } from '../../../common/utils/is-resize-observable-supported';
import styles from './ricos-viewer.scss';
const RicosViewer = withError(({ content, onShowMoreClick, disableShowMore = false, contentId }) => {
    const [experiments, ricosBiParams, instance, locale, isMobile, isSeo, isMAInstalled, host, appDefId,] = useApi((state, _actions, host) => [
        state.experiments,
        state.baseParams.biMetadata,
        getInstance(state),
        state.baseParams?.language,
        getIsMobile(state),
        getIsSeo(state),
        getIsMaInstalled(state),
        host,
        getAppDefinitionId(state),
    ]);
    const { pluginConfig, ricosTheming, contentTruncationLimit, transformCommentOnRender } = useConfiguration();
    const t = useTranslate();
    const viewerRef = React.useRef(null);
    const [hoveredMentionData, setHoverMentionData] = React.useState(null);
    const plugins = React.useMemo(() => pluginConfig
        ? initViewerPlugins(pluginConfig?.type === 'async' ? pluginConfig.plugins : pluginConfig, {
            setHoverMentionData,
        })
        : [], [pluginConfig]);
    const contentAfterConsumerTransform = transformCommentOnRender && content ? transformCommentOnRender({ content }) : content;
    const transformedContent = React.useMemo(() => getTransformedContent(contentAfterConsumerTransform, {
        truncationThreshold: disableShowMore ? undefined : contentTruncationLimit,
    }), [contentAfterConsumerTransform, disableShowMore, contentTruncationLimit]);
    const [viewerHeight, setViewerHeight] = React.useState('auto');
    const [viewerState, setViewerState] = React.useState(() => transformedContent.truncatedContent ? 'TruncatedCollapsed' : 'NonTruncated');
    React.useEffect(() => {
        if (viewerRef.current && transformedContent.truncatedContent && isResizeObserverSupported()) {
            // using observer here as we might need to update viewer height few times, as media loads and height changes
            const observer = new window.ResizeObserver((entries) => {
                setViewerHeight(entries[0].contentRect.height);
            });
            observer.observe(viewerRef.current);
            return () => {
                observer.disconnect();
            };
        }
    }, []);
    const viewerContent = viewerState === 'TruncatedCollapsed'
        ? transformedContent.truncatedContent
        : transformedContent.content;
    return (React.createElement("div", { className: styles.container },
        isMAInstalled && hoveredMentionData && hoveredMentionData.ref && (React.createElement(MemberCardLazy, { key: hoveredMentionData.mention.id, viewedMemberId: hoveredMentionData.mention.id, elementRef: hoveredMentionData.ref })),
        React.createElement("div", { className: classNames(styles.viewer, {
                [styles.truncated]: viewerState === 'TruncatedCollapsed',
                [styles.expandTransition]: viewerState === 'TruncatedExpanded',
                [styles.shrinkTransition]: viewerState === 'TruncatedCollapsed',
            }), style: { height: viewerHeight } },
            React.createElement("div", { ref: viewerRef },
                React.createElement(Viewer, { content: viewerContent, wixExperiments: experiments, plugins: plugins, locale: locale, isMobile: isMobile, seoSettings: isSeo, theme: {
                        palette: host.style.siteColors,
                        ...ricosTheming?.theme,
                        parentClass: styles.viewer,
                        customStyles: getCustomStyles(isMobile, 'viewer'),
                    }, instance: instance, biSettings: {
                        consumer: 'wixComments',
                        platform: 'Livesite',
                        defaultParams: ricosBiParams,
                        contentId,
                        postId: contentId,
                        containerId: appDefId,
                    }, linkSettings: { relValue: 'nofollow ugc' } }))),
        viewerState === 'NonTruncated' || disableShowMore ? null : (React.createElement("button", { "data-hook": SHOW_MORE_BTN, className: styles.showMore, onClick: () => {
                onShowMoreClick?.(viewerState === 'TruncatedCollapsed');
                setViewerState(viewerState === 'TruncatedCollapsed' ? 'TruncatedExpanded' : 'TruncatedCollapsed');
            } },
            React.createElement(TextExtended, { typography: "content", priority: "buttonSecondary" }, t(viewerState === 'TruncatedExpanded' ? 'comment.show-less' : 'comment.show-more')),
            viewerState === 'TruncatedExpanded' ? React.createElement(ArrowUpIcon, null) : React.createElement(ArrowDownIcon, null)))));
});
RicosViewer.displayName = 'RicosViewer';
export default RicosViewer;
//# sourceMappingURL=ricos-viewer.js.map